import { Component, ElementRef, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgbCarousel, NgbSlideEvent } from '@ng-bootstrap/ng-bootstrap';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { finalize, Subscription } from 'rxjs';
import { EnvironmentService } from 'src/app/core/services/environment/environment.service';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { Utils } from 'src/app/core/services/utils';
import { AuthData } from 'src/app/models/auth/auth-data.model';
import { AuthenticationResponse, AuthenticationUserCompany } from 'src/app/models/auth/authentication-response.model';
import { LoginStatus } from 'src/app/models/auth/login-status.enum';
import { AdminService } from 'src/app/services/admin.service';
import { ErrorMessage } from 'src/app/shared/models/error.model';
import Swal from 'sweetalert2';

import {
  ApplicationContext,
  AuthenticationRequest,
  UserType
} from '../../models';

import { CacheService } from 'src/app/services/cache.service';
import { EnumerationService } from 'src/app/services/enumeration-service';
import { validatePhoneNumber } from 'src/app/shared/components/phone-input/phone-number-validation';
import { ApplicationContextService } from '../../services/application-context.service';
import { AuthService } from '../../services/auth.service';
import { ThemeService } from '../../core/services/theme/theme.service';
import { ColorSchemePreference } from '../../core/services/theme/theme.constants';
import { Constants } from '../../services/constants';
import { NavigationService } from '../../services/navigation.service';

@Component({
    selector: 'login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    styles: [`
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
      :host ::ng-deep .tpo-loader-spin svg {
        animation: spin 1.2s linear infinite;
      }
    `],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {

  resendMethodType: 'text' | 'email' = 'text';
  sendingCode: boolean = false;
  private _shownPopover: PopoverDirective;

  @ViewChild('loginForm') loginForm: NgForm | undefined;
  @ViewChild('slider') slider: NgbCarousel;

  @ViewChild("twoFactorCodeInput")
  private _twoFactorCodeInput: ElementRef;

  @ViewChild("passwordInput")
  private _passwordInput: ElementRef;

  loginInfo: AuthenticationRequest;
  error: ErrorMessage | undefined;

  loggingIn: boolean = false;
  alreadyLoggedIn: boolean;

  companyName: string = null;
  backgroundImageUrl: string = null;
  tpoRegistrationLink: string = null;
  selectedCompanyName: string;

  availableCompanies: AuthenticationUserCompany[] = [];
  possibleLogoUrls: string[] = [];

  currentYear: number = new Date().getFullYear();

  protected setting = {
    phone: null,
    areaCode: null,
    isTwoFactor: null
  }

  // The MFA-setup pane's Save button lives in the shared action bar, outside the carousel's
  // <ng-template>, so it cannot read the phone control's validity through a template
  // reference variable the way the settings screens do. It asks the same rule directly.
  protected get canSaveMfaPhone(): boolean {
    const { phone, areaCode } = this.setting;
    if (!phone || !areaCode) {
      return false;
    }
    const country = this._enumsService.getPhoneCountryByAreaCode(areaCode);
    return !validatePhoneNumber(phone, areaCode, country);
  }


  protected needToConfirmPhone: boolean = false;

  protected verificationCode: string;

  protected passwordRequired: boolean = false;

  protected currentSlideId: string = 'userNameEntry';

  protected companyIdOverride: number | null = null;
  protected secretKeyHitForCompanyOverride: boolean = false;

  // DEV ENV ONLY short-session durations (minutes) surfaced alongside the company-override field. The server
  // only honors these in non-production (DV) environments and always clamps them to strictly less than the
  // configured maximum, so this can only ever REDUCE session length (never extend it) — purely a convenience
  // for exercising the max-session warning/countdown quickly.
  protected readonly debugShortSessionOptions: number[] = [6, 10, 15, 30];

  private _returnUrl: string | null = null;

  private readonly _adminService = inject(AdminService);
  private readonly _authService = inject(AuthService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _navigationService = inject(NavigationService);
  private readonly _applicationContext = inject(ApplicationContextService);
  private readonly _router = inject(Router);
  private readonly _environment = inject(EnvironmentService);
  private readonly _localStorageService = inject(LocalStorageService);
  private readonly _themeService = inject(ThemeService);

  // The sign-in page shows the theme the last session ended in; its switch is offered when that user could use dark mode.
  readonly appearance$ = this._themeService.appearance$;
  readonly appearanceAvailable$ = this._themeService.available$;
  private readonly _enumsService = inject(EnumerationService);
  private readonly _cacheService = inject(CacheService);

  private readonly _activatedRouteSubscription: Subscription;
  private _applicationContextSubscription: Subscription;

  private _queryParams: string = "";

  constructor() {
    this.possibleLogoUrls = this._navigationService.possibleLogoUrls;
    this.backgroundImageUrl = this._environment.apiInfo.apiBaseUrl + 'company/co/login';
    this.loginInfo = new AuthenticationRequest();
    this.loginInfo.usernameValidationOnly = true;
    this.loginInfo.scope = Constants.authorization.adminScope;

    this._activatedRouteSubscription = this._activatedRoute.queryParams.subscribe((params) => {
      const companyGuid = params[Constants.authorization.companyGuid];
      this.loginInfo.companyGuid = companyGuid;
      this._returnUrl = params['returnUrl'];

      const userName = params['username'];
      if (userName) {
        this.loginInfo.username = userName;
      }
      Object.keys(params).forEach(key => {
        if (key !== 'returnUrl') {
          this._queryParams += `${key}=${params[key]}&`;
        } else {
          this._queryParams += `r_url=${params[key]}&`;
        }
      });
      if (this._queryParams && this._queryParams.endsWith('&')) {
        this._queryParams = this._queryParams.slice(0, -1);
      }
    });
  }

  ngOnInit() {
    const authData = this._localStorageService.authorizationData;
    if (authData && authData.token) {
      this.alreadyLoggedIn = true;
      this._applicationContextSubscription?.unsubscribe();
      this._applicationContextSubscription = this._applicationContext.context.subscribe(context => {
        this.onAfterLogin(context);
      }, error => {
        console.error(error);
        this.alreadyLoggedIn = false;
      });
    }

    this._adminService.getTpoRegistrationLink().subscribe({
      next: (res) => {
        this.tpoRegistrationLink = res?.tpoUserRegistrationURL || null;
      }
    });

  }

  ngOnDestroy() {
    this._activatedRouteSubscription?.unsubscribe();
    this._applicationContextSubscription?.unsubscribe();
  }

  onChangeNumberClicked = () => {
    this.needToConfirmPhone = false;
  }

  onConfirmPhoneClicked = () => {
    this.loggingIn = true;
    this._authService.confirmUpdatePhoneAnonymous(this.setting.phone, this.setting.areaCode, this.verificationCode, this.loginInfo)
      .subscribe({
        next: () => {
          this.onLoginClicked();
        },
        error: (error) => {
          this.loggingIn = false;
          if (error?.code === "InvalidToken") {
            this.setLoginError(error.description);
          } else {
            this.setLoginError(`Couldn't process the confirmation code.`);
          }
        }
      });
  }

  onSaveNumberClicked() {
    this.loggingIn = true;
    this._authService.updatePhoneAnonymous(this.setting.phone, this.setting.areaCode, this.loginInfo)
      .pipe(finalize(() => this.loggingIn = false))
      .subscribe({
        next: (res) => {
          this.needToConfirmPhone = true;
        },
        error: (error) => {
          this.setLoginError(error?.message || 'Unable to update phone number');
        }
      });
  }

  onLoginClicked = () => {
    if (this.passwordRequired && this.loginInfo.password) {
      this.passwordRequired = false;
    }
    this.error = undefined;
    this.doLogin();
  };

  onLetsGoClickedOnCompanySelectionPage = () => {
    this.onAvailableCompanyPickedForLogin();
  }

  onBackClickedOnPasswordPage = () => {
    this.onCancelLoginClicked();
  }

  onCancelLoginClicked = () => {
    this.error = undefined;
    this.slider.select('userNameEntry');
    this.loginInfo.companyGuid = null;
    this.loginInfo.userCompanyGuid = null;
    this.loginInfo.twoFactorCode = null;
    this.loginInfo.scope = null;
    this.loginInfo.username = null;
    this.loginInfo.password = null;
    this.selectedCompanyName = "";
    this.passwordRequired = false;
    this.loginInfo.usernameValidationOnly = true;
    this.loginInfo.debugShortSessionInMinutes = null;

    this.loginForm.form.markAsUntouched();

    this.needToConfirmPhone = false;
    this.setting.phone = null;
    this.setting.areaCode = null;
    this.verificationCode = null;
  }

  onForgotPasswordClicked = () => {
    this.error = undefined;
    this._router.navigate(['forgot-password'], {
      queryParams: {
        scope: this.loginInfo.scope,
        email: this.loginInfo.username
      },
    });
  };

  onAvailableCompanyPickedForLogin = () => {
    if (!this.loginInfo.userCompanyGuid) {
      this.loginInfo.scope = null;
      this.loginInfo.companyGuid = null;
      this.selectedCompanyName = "";
      return;
    }
    const companyThatIsPicked = this.availableCompanies.find(company => company.userCompanyGuid == this.loginInfo.userCompanyGuid);
    if (companyThatIsPicked) {
      this.loginInfo.scope = companyThatIsPicked.userType;
      this.loginInfo.companyGuid = companyThatIsPicked.companyGuid;
      this.loginInfo.userCompanyGuid = companyThatIsPicked.userCompanyGuid;
      this.selectedCompanyName = companyThatIsPicked.companyName;

      if (companyThatIsPicked.isExternalAuth) {
        this.error = undefined;
        this.redirectToExtternalAuthProvider();
        return;
      }

      if (companyThatIsPicked.isSamlAuth) {
        this.error = undefined;
        this.redirectToSamlProvider(companyThatIsPicked);
        return;
      }

      this.passwordRequired = true;
      this.slider.select('passwordEntry');
    }
  }

  onAppearanceChosen = (preference: ColorSchemePreference) => {
    this._themeService.choose(preference);
  }

  onTestBackgroundImageErrored = () => {
    this.backgroundImageUrl = "./assets/images/background-temp.png";
  }

  onCarouselTransitionComplete = (e: NgbSlideEvent) => {
    if (e.current === 'twoFactorCodeEntry') {
      this._twoFactorCodeInput.nativeElement.focus();
    }
    if (e.current === 'passwordEntry') {
      this._passwordInput.nativeElement.focus();
    }
  }

  onShownPopover(ref: PopoverDirective) {
    const setRef = () => this._shownPopover = ref;

    if (this._shownPopover) {
      this._shownPopover.hide();
      setTimeout(setRef);
    } else {
      setRef();
    }
  }

  onHiddenPopover() {
    this._shownPopover = undefined;
  }

  onSendCodeClicked = (method: 'text' | 'email' = 'text') => {
    this.sendingCode = true;
    this.loginInfo.twoFactorMethod = method === 'email' ? 'Email' : 'Text';

    const closePopover = () => {
      try {
        const popoverElements = document.querySelectorAll('.popover');
        popoverElements.forEach(el => {
          (el as HTMLElement).style.display = 'none';
        });
        // Closing the popover via DOM is not enough — ngx-bootstrap keeps
        // its own state, and a document click is what dismisses it cleanly.
        document.body.click();
      } catch (e) {
        console.error('Error closing popover:', e);
      }
    };

    const onCompleted = (messageText: string) => {
      this.sendingCode = false;
      closePopover();

      // Wait for the popover to fully dismiss before opening Swal —
      // overlapping overlays cause focus-trap conflicts.
      setTimeout(() => {
        Swal.fire('Sent Code', messageText, 'success');
      }, 100);
    };

    const onError = (err: any) => {
      this.sendingCode = false;
      console.error('Error sending code:', err);
    };

    if (method === 'text') {
      this._authService.sendTwoFactorPhoneCode(this.loginInfo.userCompanyGuid).subscribe({
        next: () => onCompleted('We sent another code to your phone. Please check your phone and use that code to login.'),
        error: onError
      });
    } else if (method === 'email') {
      this._authService.sendTwoFactorEmailCode(this.loginInfo.userCompanyGuid).subscribe({
        next: () => onCompleted('We sent another code to your email. Please check your inbox and use that code to login.'),
        error: onError
      });
    }
  }

  private normalizeTwoFactorMethod(method: string | number | null | undefined): 'Text' | 'Email' {
    return method === 'Email' || method === 1 ? 'Email' : 'Text';
  }

  // Ctrl+Shift+C on the password input reveals a company-id override field.
  // Dev/local only — production users can't trigger it.
  protected onKeyDownOnPasswordInput = (event: KeyboardEvent) => {
    if (this._environment.production) return;
    if (event.ctrlKey && event.shiftKey && event.key === 'C') {
      event.preventDefault();
      this.secretKeyHitForCompanyOverride = true;
    }
  }

  private doLogin = () => {
    if (this.loginForm) {
      this.loginForm.form.markAllAsTouched();
      if (this.loginForm.form.valid) {
        this.loggingIn = true;
        this._authService.signIn(this.loginInfo).subscribe(
          (response: AuthenticationResponse) => {

            if (response.userCompanyGuid) {
              this.loginInfo.userCompanyGuid = response.userCompanyGuid;
            }

            if (response.loginStatus === LoginStatus.Error) {
              this.onLoginErrored(response);
            } else if (response.loginStatus === LoginStatus.UsernameValidationComplete) {
              this.onUserNameValidationComplete(response.availableCompanies);
            } else if (response.loginStatus === LoginStatus.TwoFactorAuthenticationSetupRequired) {
              if (!this.loginInfo.userCompanyGuid && response.availableCompanies.length > 0) {
                this.loginInfo.userCompanyGuid = response.availableCompanies[0].userCompanyGuid;
              }
              this.onMfaSetupRequired();
            } else if (response.loginStatus === LoginStatus.CompanySelectionRequired) {
              this.onCompanySelectionRequired(response.availableCompanies);
            } else if (response.loginStatus === LoginStatus.TwoFactorAuthenticationRequired) {
              if (!this.loginInfo.userCompanyGuid && response.availableCompanies.length > 0) {
                this.loginInfo.userCompanyGuid = response.availableCompanies[0].userCompanyGuid;
              }
              this.loginInfo.twoFactorMethod = this.normalizeTwoFactorMethod(response.twoFactorMethod);
              this.resendMethodType = this.loginInfo.twoFactorMethod === 'Email' ? 'email' : 'text';
              this.onTwoFactorCodeRequired();
            } else if (response.loginStatus === LoginStatus.Success) {
              this.persistAuthData(response);
              this._applicationContextSubscription = this._applicationContext.context.subscribe(this.onAfterLogin);
            }
          },
          (error) => {
            this.companyName = null;
            this.loginInfo.companyGuid = null;
            this.loginInfo.userCompanyGuid = null;
            this.setLoginError('Unable to login at the moment, please try again later.');
            this.loggingIn = false;
          }
        );
      }
    }
  }

  private redirectToExtternalAuthProvider = () => {
    const scope = this.loginInfo.scope || 'Admin';
    if (this._queryParams) {
      this._queryParams += `&scope=${scope}`;
    }
    const returnUrl = `${window.location.protocol}//${window.location.host}/ext-auth-callback?${this._queryParams}`;

    const challengeUrl = this._environment.apiInfo.authApiBaseUrl + `api/auth/connect/challenge/${this.loginInfo.companyGuid}/${scope}?returnUrl=${encodeURIComponent(returnUrl)}&userName=${encodeURIComponent(this.loginInfo.username ?? '')}`;
    window.location.href = challengeUrl;
  }

  private redirectToSamlProvider = (company: AuthenticationUserCompany) => {
    const idpName = company.samlProviderName;
    if (!idpName) {
      this.setLoginError('SAML provider is not configured for this account.');
      return;
    }

    const url = this._environment.apiInfo.apiBaseUrl
      + `api/auth/saml-sso/login/${company.companyGuid}/${encodeURIComponent(idpName)}`
      + `?scope=${company.userType}`
      + `&userCompanyGuid=${encodeURIComponent(company.userCompanyGuid ?? '')}`
      + `&returnUrl=${encodeURIComponent(this._returnUrl ?? '')}`;
    window.location.href = url;
  }

  private onAfterLogin = (context: ApplicationContext) => {
    // If the user's account is paused, redirect to the account-paused page
    if (context.userPermissions?.accountPaused) {
      this._navigationService.navigateToPath('account-paused');
      return;
    }

    if (this._returnUrl) {
      if (this.tryResumeSamlIdpSso(this._returnUrl)) {
        return;
      }

      if (this.isAbsoluteUrl(this._returnUrl)) {
        window.location.href = this._returnUrl;
        return;
      }

      let returnUrl = this._returnUrl;
      if (!this._returnUrl.includes('admin') && !this._returnUrl.includes('tpo')) {
        returnUrl = '/admin/' + this._returnUrl;
      }
      this._navigationService.navigateToPath(returnUrl);
      return;
    }
    // TODO: Kaan - investigate what the below is for??
    if (this._activatedRoute.snapshot.queryParams.redirect) {
      this._navigationService.navigateToPath(this._activatedRoute.snapshot.queryParams.redirect);
      return;
    }
    if (context.currentlyLoggedInUser.userType === UserType.Tpo) {
      this._navigationService.navigateToPath('tpo/dashboard');
    } else if (context.userPermissions.companyId === 1) {
      this._navigationService.navigateToPath('global-admin/companies');
    } else {
      this._navigationService.navigateToPath('admin/dashboardv2');
    }
    if (!this._environment.production && this.companyIdOverride && this.secretKeyHitForCompanyOverride) {
      this._applicationContext.setCompanyIdOverride(this.companyIdOverride);
      localStorage.setItem('companyIdOverride', this.companyIdOverride.toString());
    } else {
      this._applicationContext.setCompanyIdOverride(null);
      localStorage.removeItem('companyIdOverride');
    }
  };

  private onLoginErrored = (response: AuthenticationResponse) => {
    if (response.errorMessage === 'Bad username or password') {
      this.loginInfo.password = null;
      this.passwordRequired = true;
      this.loginForm.form.markAsUntouched();
    }
    this.setLoginError(response.errorMessage);
    this.loggingIn = false;
  }

  private onTwoFactorCodeRequired = () => {
    this.loginInfo.usernameValidationOnly = false;
    this.slider.select('twoFactorCodeEntry');
    this.loggingIn = false;
  }

  private onCompanySelectionRequired = (availableCompanies: AuthenticationUserCompany[]) => {
    this.loggingIn = false;
    this.loginInfo.usernameValidationOnly = false;
    this.availableCompanies = availableCompanies;
    if (availableCompanies.length == 1) {
      this.loginInfo.userCompanyGuid = availableCompanies[0].userCompanyGuid;
      this.loginInfo.companyGuid = availableCompanies[0].companyGuid;
      this.onAvailableCompanyPickedForLogin();
      return;
    }
    if (this.slider) {
      this.slider.select('companySelection');
    }
  }

  private onUserNameValidationComplete = (availableCompanies: AuthenticationUserCompany[]) => {
    this.loggingIn = false;
    this.loginInfo.usernameValidationOnly = false;
    this.availableCompanies = availableCompanies;
    this.loginInfo.userCompanyGuid = availableCompanies[0].userCompanyGuid;
    this.onAvailableCompanyPickedForLogin();
  }

  private onMfaSetupRequired = () => {
    this.passwordRequired = false;
    this.loginInfo.usernameValidationOnly = false;
    this.slider.select('mfaSetup');
    this.loggingIn = false;
  }

  private setLoginError = (msg: string) => {
    this.error = new ErrorMessage('Unable to login!', msg);
  };

  private persistAuthData = (authResponse: AuthenticationResponse): void => {

    const jwtPayload = Utils.parseJwt(authResponse.jwtToken);

    const authData: AuthData = {
      companyGuid: this.loginInfo.companyGuid,
      userCompanyGuid: this.loginInfo.userCompanyGuid,
      token: authResponse.jwtToken,
      role: jwtPayload.AspNetRole,
      userName: authResponse.userName,
      refreshToken: authResponse.refreshToken,
      companies: this.availableCompanies,
      rememberMe: this.loginInfo.rememberMe,
      expiresAt: Utils.getTokenExpireDate(jwtPayload.exp)
    };
    this._cacheService.setTenantPrefix(authData.userCompanyGuid);

    this._localStorageService.authorizationData = authData;

    // Clear TV-auth session flag on normal login so notification prompts work
    try {
      localStorage.removeItem('lodasoft_tv_auth_session');
    } catch (e) {
      // localStorage may not be available
    }
  }

  private isAbsoluteUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  /**
   * IdP resume: POST access_token to the API SSO resume URL so SamlIdpSsoTokenAuthExtensions can authenticate it.
   * Restricted to the configured API origin and the SSO path — never blind-POST the token to an arbitrary URL.
   */
  private tryResumeSamlIdpSso(returnUrl: string): boolean {
    if (!this.isAbsoluteUrl(returnUrl)) {
      return false;
    }

    let parsed: URL;
    try {
      parsed = new URL(returnUrl);
    } catch {
      return false;
    }

    if (!parsed.pathname.startsWith('/api/auth/saml-sso/idp/sso')) {
      return false;
    }

    const apiOrigins = [
      this._environment.apiInfo.apiBaseUrl,
      this._environment.apiInfo.authApiBaseUrl
    ]
      .filter((u): u is string => !!u)
      .map(u => {
        try { return new URL(u).origin; } catch { return null; }
      })
      .filter((o): o is string => !!o);

    if (!apiOrigins.some(origin => origin.toLowerCase() === parsed.origin.toLowerCase())) {
      return false;
    }

    const token = this._localStorageService.authorizationData?.token;
    if (!token) {
      return false;
    }

    const form = document.createElement('form');
    form.method = 'POST';
    form.action = returnUrl;
    form.style.display = 'none';

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'access_token';
    input.value = token;
    form.appendChild(input);

    document.body.appendChild(form);
    form.submit();
    return true;
  }
}
